import { Link, useRouterState } from '@tanstack/react-router'
import { BookOpenText, Command, Drama, Film, House, LibraryBig, LogOut, Monitor, Moon, Mountain, Settings, Sparkles, Sun, Wand2 } from 'lucide-react'
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'
import { signOut } from '@/features/auth/AuthGate'
import { describeCombo } from '@/lib/keys'
import { useUiStore, type Theme } from '@/stores/uiStore'

/**
 * Layout frame: sticky top nav, an optional banner row (AI health), the page,
 * and a slot for the bottom queue strip. The document scrolls; pages never
 * introduce their own scroll container unless a panel truly must.
 *
 * Navigation is the places a parent needs (docs/SKEL-STORY.md P8, R7): **Home · Puppets · Sprites ·
 * Backgrounds · Actions · Stories · Library**, the primary **Make a story**, and Settings behind
 * the gear (Activity and Vocabulary live under Settings › Advanced). The stage annotator
 * (`/annotate/stage/…`) belongs to Backgrounds; character annotation belongs to Sprites.
 * Library is every picture, character, object and sound.
 */

export interface NavItem {
  to: '/' | '/puppets' | '/sprites' | '/backgrounds' | '/actions' | '/stories' | '/library'
  label: string
  icon: ReactNode
  /** Path prefixes that also light this item up (the puppet editor lives under `/skel`, the stage annotator under `/annotate`). */
  match: readonly string[]
  /** Only the exact path (Home). */
  exact?: boolean
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Home', icon: <House aria-hidden />, match: ['/'], exact: true },
  { to: '/puppets', label: 'Puppets', icon: <Drama aria-hidden />, match: ['/puppets', '/skel'] },
  { to: '/sprites', label: 'Sprites', icon: <Film aria-hidden />, match: ['/sprites', '/annotate/character'] },
  { to: '/backgrounds', label: 'Backgrounds', icon: <Mountain aria-hidden />, match: ['/backgrounds', '/annotate/stage'] },
  { to: '/actions', label: 'Actions', icon: <Wand2 aria-hidden />, match: ['/actions'] },
  { to: '/stories', label: 'Stories', icon: <BookOpenText aria-hidden />, match: ['/stories'] },
  { to: '/library', label: 'Library', icon: <LibraryBig aria-hidden />, match: ['/library'] },
]

/** Whether `pathname` belongs to a nav item. `/stories/new` belongs to Stories. */
export function isNavActive(item: Pick<NavItem, 'match' | 'exact'>, pathname: string): boolean {
  return item.match.some((m) => (item.exact ? pathname === m : pathname === m || pathname.startsWith(`${m}/`)))
}

export interface AppShellProps {
  banner?: ReactNode
  strip?: ReactNode
  children: ReactNode
}

export function AppShell({ banner, strip, children }: AppShellProps) {
  const queueOpen = useUiStore((s) => s.queuePanelOpen)
  const queueHeight = useUiStore((s) => s.panels.queuePanel)
  // Reserve room for the strip (40 px) and, when expanded, the panel.
  const bottomPad = 40 + (queueOpen ? queueHeight : 0)
  return (
    // `--shell-bottom`: what the fixed strip covers, for sticky footers that must stay above it.
    <div className="flex min-h-dvh flex-col bg-paper text-ink" style={{ ['--shell-bottom' as string]: `${bottomPad}px` } as CSSProperties}>
      <TopNav />
      {banner}
      <main className="flex-1">
        {children}
      </main>
      {strip}
    </div>
  )
}

const NAV_LINK =
  'inline-flex h-10 shrink-0 items-center gap-2 rounded-xl px-2.5 text-sm font-medium text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:focus-ring sm:px-3 [&_svg]:size-4 data-[active=true]:bg-accent-soft data-[active=true]:text-ink'

function TopNav() {
  const setPaletteOpen = useUiStore((s) => s.setPaletteOpen)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const settingsActive = pathname === '/settings' || pathname.startsWith('/settings/') || pathname === '/jobs'
  return (
    <header className="sticky top-0 z-40 border-b border-line bg-paper/90 backdrop-blur supports-[backdrop-filter]:bg-paper/75">
      <div className="mx-auto flex h-14 w-full max-w-screen-2xl items-center gap-1 px-2 sm:gap-2 sm:px-4">
        <Link to="/" className="mr-1 inline-flex h-10 items-center gap-2 rounded-xl px-2 text-base font-semibold text-ink hover:bg-paper-2 focus-visible:focus-ring sm:mr-2" aria-label="Story Studio home">
          <span className="inline-flex size-7 items-center justify-center rounded-lg bg-accent text-accent-ink">
            <Sparkles className="size-4" aria-hidden />
          </span>
          <span className="hidden lg:inline">Story Studio</span>
        </Link>

        <nav aria-label="Primary" className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto sm:gap-1">
          {NAV_ITEMS.map((item) => {
            const active = isNavActive(item, pathname)
            return (
              <Link
                key={item.label}
                to={item.to}
                activeOptions={{ exact: true }}
                activeProps={{ className: '' }}
                data-active={active}
                aria-current={active ? 'page' : undefined}
                className={NAV_LINK}
              >
                {item.icon}
                <span className="sr-only sm:not-sr-only">{item.label}</span>
              </Link>
            )
          })}
        </nav>

        <div className="flex items-center gap-1">
          <Button asChild variant="primary" size="sm" className="px-2.5 sm:px-3">
            <Link to="/stories/new" aria-label="Make a story">
              <Sparkles aria-hidden />
              <span className="hidden sm:inline">Make a story</span>
            </Link>
          </Button>
          <WithTooltip label={`Command palette (${describeCombo('$mod+k')})`}>
            <Button variant="ghost" size="icon" className="hidden sm:inline-flex" aria-label="Open command palette" onClick={() => setPaletteOpen(true)}>
              <Command aria-hidden />
            </Button>
          </WithTooltip>
          <ThemeMenu />
          <WithTooltip label="Settings">
            <Button asChild variant="ghost" size="icon" className={cn(settingsActive && 'bg-accent-soft')}>
              <Link to="/settings" aria-label="Settings" aria-current={settingsActive ? 'page' : undefined} data-testid="nav-settings">
                <Settings aria-hidden />
              </Link>
            </Button>
          </WithTooltip>
          <SignOutButton />
        </div>
      </div>
    </header>
  )
}

/** Shown only when the server enforces a sign-in (`/api/auth/me` → `auth_enabled`). */
function SignOutButton() {
  const [enabled, setEnabled] = useState(false)
  useEffect(() => {
    let live = true
    fetch('/api/auth/me', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((me: { auth_enabled?: boolean } | null) => live && setEnabled(Boolean(me?.auth_enabled)))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])
  if (!enabled) return null
  return (
    <WithTooltip label="Sign out">
      <Button variant="ghost" size="icon" aria-label="Sign out" onClick={() => void signOut()}>
        <LogOut aria-hidden />
      </Button>
    </WithTooltip>
  )
}

const THEME_ICON: Record<Theme, ReactNode> = { system: <Monitor aria-hidden />, light: <Sun aria-hidden />, dark: <Moon aria-hidden /> }

function ThemeMenu() {
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Theme: ${theme}`}>
          {THEME_ICON[theme]}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/* ------------------------------------------------------------------------ */

export interface PagePlaceholderProps {
  title: string
  /** Which build phase fills this page (shown as a small label). */
  phase: string
  description: string
  /** The validated search/params, echoed so deep links can be checked by eye. */
  details?: Record<string, unknown>
  actions?: ReactNode
  children?: ReactNode
  className?: string
}

/**
 * Clearly-labelled stand-in for pages that a later phase replaces. Renders
 * the validated route params so the routing contract can be exercised now.
 */
export function PagePlaceholder({ title, phase, description, details, actions, children, className }: PagePlaceholderProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const shown = details ? Object.entries(details).filter(([, v]) => v !== undefined && v !== null && v !== '') : []
  return (
    <div className={cn('mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8', className)}>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-3">{phase}</p>
          <h1 className="text-3xl font-semibold tracking-tight text-ink">{title}</h1>
          <p className="max-w-2xl text-sm text-ink-2">{description}</p>
        </div>
        {actions}
      </header>
      <section className="card flex flex-col gap-3 border-dashed p-5" aria-label="Placeholder">
        <p className="text-sm text-ink-2">
          Placeholder — this screen is built in a later phase. Route <code className="rounded bg-paper-2 px-1.5 py-0.5 font-mono text-xs">{pathname}</code> is wired and its
          parameters validate.
        </p>
        {shown.length ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {shown.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-mono text-xs text-ink-3">{k}</dt>
                <dd className="truncate font-mono text-xs text-ink">{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </section>
      {children}
    </div>
  )
}
